import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import {
  TEXT_TYPE,
  UPLOAD_OR_REPLACE_TYPES,
} from '@/constants/constants.js';
import TextInput from '@/components/TextInput.js';
import SelectInput from '@/components/SelectInput.js';
import { audioSpeedOptions } from '@/constants/dropdownOptions.js';
import { generateAudio } from '@/features/orders/ordering/services/orderMediaService.js';
import LoadingIndicator from '@/components/LoadingIndicator.js';
import {
  MIN_CLIP_DURATION,
  MAX_CLIP_DURATION,
} from '../../workflow/components/upload/audioComponents/AudioTrimmer.js';

const DuplicateAudio = ({
  audio_type,
  client_id,
  label,
  accept,
  descriptions = [],
  descriptionError,
  required = true,
  clientInfo,
  aiAudios,
  setAIAudios,
}) => {
  const user_id = sessionStorage.getItem('userid');
  const [audio, setAudio] = useState({
    id:
      aiAudios.find((item) => item.text_type === TEXT_TYPE.SPEECH)?.id ?? null,
    content: null,
    description: '',
    speed: 0.8,
    generateText: clientInfo.speech_text,
  });
  const fileInputRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState('');
  const audioRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [whichAudioType, setWhichAudioType] = useState(TEXT_TYPE.SPEECH);

  const handleSelectAudioType = useCallback(
    (type) => {
      setWhichAudioType(type);
      const matchingAudio = aiAudios.find((item) => item.text_type === type);
      const _id = matchingAudio?.id || null;
      const _generateText =
        type === TEXT_TYPE.SPEECH
          ? clientInfo.speech_text
          : clientInfo.farewell_text;

      setAudio((prevAudio) => ({
        ...prevAudio,
        id: _id,
        generateText: _generateText,
      }));
    },
    [aiAudios, clientInfo.speech_text, clientInfo.farewell_text]
  );

  const getAudioDuration = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      audio.src = URL.createObjectURL(file);
      audio.onloadedmetadata = () => {
        if (!isNaN(audio.duration)) {
          resolve(audio.duration);
        } else {
          reject('无法获取音频时长');
        }
      };
      audio.onerror = () => {
        reject('音频文件加载失败');
      };
    });
  }, []);

  const validateAudioFiles = useCallback(
    async (newFiles) => {
      const errorMessages = [];
      const validFiles = [];

      for (const file of newFiles) {
        const isValidFormat = /\.(mp3|wav|ogg|m4a|flac|aac)$/i.test(file.name);
        if (!isValidFormat) {
          errorMessages.push('·音频格式要求: MP3、WAV、OGG、M4A、FLAC、AAC! ');
          continue;
        }
        try {
          const duration = await getAudioDuration(file);
          if (
            duration &&
            duration > MAX_CLIP_DURATION &&
            duration < MIN_CLIP_DURATION
          ) {
            errorMessages.push(
              `音频时长不能少于 ${MIN_CLIP_DURATION} 秒,超过 ${
                MAX_CLIP_DURATION / 60
              } 分钟`
            );
          } else if (duration) {
            validFiles.push(file);
          } else {
            errorMessages.push('·无法获取音频时长，请使用 MP3/WAV 等格式! ');
          }
        } catch (error) {
          console.error(error);
          errorMessages.push(error);
        }
      }

      return { validFiles, errorMessages };
    },
    [getAudioDuration]
  );

  const uploadFile = useCallback(
    async (file, client_id, uploadOrReplace) => {
      const formData = new FormData();
      formData.append('id', audio.id);
      formData.append('user_id', user_id);
      formData.append('audio_type', audio_type);
      if (client_id) formData.append('client_id', client_id);
      formData.append('audio_file', file);
    },
    [audio, audio_type, user_id]
  );

  const processValidFiles = useCallback(
    async (validFiles, errorMessages, uploadOrReplace) => {
      if (validFiles.length > 0) {
        setAudio({
          ...audio,
          content: validFiles[0],
        });
        setErrorMessage('');
      } else {
        setErrorMessage(errorMessages.join(' '));
      }
    },
    [audio]
  );

  const handleFileUpload = useCallback(
    async (event, uploadOrReplace) => {
      const newFiles = Array.from(event.target.files);
      try {
        const validationResult = await validateAudioFiles(newFiles);
        await processValidFiles(
          validationResult.validFiles,
          validationResult.errorMessages,
          uploadOrReplace
        );
      } catch (error) {
        console.error('文件上传失败:', error);
        throw new Error('文件上传失败，请稍后重试');
      }
    },
    [processValidFiles, validateAudioFiles]
  );

  const handleFileReplace = useCallback(
    async (event, uploadOrReplace) => {
      const newFiles = Array.from(event.target.files);
      try {
        const validationResult = await validateAudioFiles(newFiles);
        const { validFiles, errorMessages } = validationResult;

        if (validFiles.length > 0) {
          const newFile = validFiles[0];
          const ossUrl = await uploadFile(newFile, client_id, uploadOrReplace);
          const _audio = { ...audio, content: ossUrl, description: '' };
          setAudio(_audio);
          setErrorMessage('');
        } else {
          setErrorMessage(errorMessages.join(' '));
        }
      } catch (error) {
        console.error('文件替换失败:', error);
        throw new Error('文件替换失败，请稍后重试');
      }
    },
    [validateAudioFiles, uploadFile, client_id, audio]
  );

  const handleUpdateDescription = useCallback(
    async (e) => {
      const newAudio = {
        ...audio,
        description: e.target.value,
      };
      setAudio(newAudio);
    },
    [audio]
  );

  const handleUpdateGenerateText = useCallback(
    async (e) => {
      const newAudio = {
        ...audio,
        generateText: e.target.value,
      };
      setAudio(newAudio);
    },
    [audio]
  );

  const handleSelectSpeed = useCallback(
    (value) => {
      setAudio({
        ...audio,
        speed: value,
      });
    },
    [audio, setAudio]
  );

  const handleRegenerate = async (audio) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append('batch_id', clientInfo.batch_id);
    if (audio.id) {
      formData.append('id', audio.id);
    }
    formData.append('audio_file', audio.content);
    formData.append('description', audio.description);
    formData.append('generateText', audio.generateText);
    formData.append('text_type', whichAudioType);
    formData.append('client_id', client_id);
    formData.append('speed', audio.speed);
    try {
      const response = await generateAudio(formData);
      if (audio.id) {
        setAIAudios((prevAIAudios) => {
          const updatedAIAudios = prevAIAudios.map((item) => {
            if (item.id === audio.id) {
              return { ...item, audio_AI_url: response.data.url };
            }
            return item;
          });
          return updatedAIAudios;
        });
      } else {
        setAIAudios((prevAIAudios) => {
          const newAIAudio = {
            id: response.data.id,
            audio_AI_url: response.data.url,
            text_type: whichAudioType,
            batch_id: clientInfo.batch_id,
          };
          return [...prevAIAudios, newAIAudio];
        });
      }

      // setAudioUrl(response.data.url);
      // setNewSentenceText(new_sentence_text);
      setIsLoading(false);
    } catch (error) {
      console.error('重新生成失败', error);
    } finally {
      setIsLoading(false); // 无论成功还是失败，都关闭加载状态
    }
  };

  const uploadItem = useMemo(() => {
    if (audio.content) {
      return (
        <div className={`file-item audio`}>
          <div className="file-container">
            <audio
              ref={audioRef}
              src={
                audio.content instanceof File
                  ? URL.createObjectURL(audio.content)
                  : audio.content
              }
              controls
              className="audio-player"
            />
            <div className="file-operation-container">
              <input
                type="file"
                className="form-control"
                accept={accept}
                style={{ display: 'none' }}
                onChange={(event) =>
                  handleFileReplace(event, UPLOAD_OR_REPLACE_TYPES.replace)
                }
              />
            </div>
          </div>
          <div className="file-description-container">
            <div className={`file-description`}>
              <div className="description-title-container">
                <span style={{ color: 'red', marginRight: '0.25rem' }}>*</span>
                <span className="description-title">声音文本</span>
              </div>
              <div className="col-md-12">
                <input
                  type="text"
                  className="form-control"
                  value={audio.description}
                  onChange={handleUpdateDescription}
                />
                {!descriptions[0] && (
                  <div className="error">{descriptionError}</div>
                )}
              </div>
            </div>
          </div>
          <div className="file-description-container">
            <div className={`file-description`}>
              <div className="description-title-container">
                <span style={{ color: 'red', marginRight: '0.25rem' }}>*</span>
                <span className="description-title">生成音频语速</span>
              </div>
              <SelectInput
                name="videoType"
                options={audioSpeedOptions}
                value={audio.speed}
                onChange={(e) => {
                  handleSelectSpeed(e.target.value);
                }}
                className="col-md-5"
              />
            </div>
          </div>
        </div>
      );
    }
    return null;
  }, [
    audio.content,
    audio.description,
    audio.speed,
    accept,
    handleUpdateDescription,
    descriptions,
    descriptionError,
    handleFileReplace,
    handleSelectSpeed,
  ]);

  const generatedAudioItem = useMemo(
    () =>
      aiAudios.map((item) => (
        <div className={`file-item audio`}>
          {item.text_type === TEXT_TYPE.SPEECH ? '致辞' : '告别'}
          <div className="file-container">
            <audio
              ref={audioRef}
              src={item.audio_AI_url}
              controls
              className="audio-player"
            />
          </div>
        </div>
      )),
    [aiAudios]
  );

  return (
    <div className="duplicate-sound-modal">
      <div className="file-list-container">{generatedAudioItem}</div>
      <div className="duplicate-sound-header">
        <ButtonGroup>
          <button
            type="button"
            className={
              whichAudioType === TEXT_TYPE.SPEECH
                ? 'btn btn-primary'
                : 'btn btn-outline-primary'
            }
            onClick={() => {
              handleSelectAudioType(TEXT_TYPE.SPEECH);
            }}
          >
            生成致辞音频
          </button>
          <button
            type="button"
            className={
              whichAudioType === TEXT_TYPE.FAREWELL
                ? 'btn btn-primary'
                : 'btn btn-outline-primary'
            }
            onClick={() => handleSelectAudioType(TEXT_TYPE.FAREWELL)}
          >
            生成告别音频
          </button>
        </ButtonGroup>
      </div>
      <div className="audio-container">
        <div className="col-md-12">
          <div className="mb-3">
            <div className="audio-upload-section">
              <div className="input-section-container">
                <div className="form-label-container">
                  <h5>
                    {label}{' '}
                    {required && <span style={{ color: 'red' }}> *</span>}
                  </h5>
                </div>
                <div className="audio-type-container">
                  <TextInput
                    label="生成文本"
                    type="textarea"
                    value={audio.generateText}
                    checked={whichAudioType === 'audio'}
                    onChange={handleUpdateGenerateText}
                    required={false}
                    style={{ minHeight: '10rem' }}
                  />
                </div>
                <Button
                  type="Button"
                  className="add-audio-button"
                  onClick={() => {
                    if (audio.content === null) {
                      fileInputRef.current.click();
                    } else {
                      fileInputRef.current.click();
                    }
                  }}
                >
                  选择音频文件
                </Button>
              </div>
              <div className="file-list-container">{uploadItem}</div>
              <div className="file-upload-container">
                {errorMessage && (
                  <div className="error-message">{errorMessage}</div>
                )}
                <input
                  type="file"
                  accept={accept}
                  className="form-control"
                  multiple
                  onChange={(event) => {
                    handleFileUpload(event, UPLOAD_OR_REPLACE_TYPES.upload);
                  }}
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="btn-container">
          {isLoading ? (
            <LoadingIndicator />
          ) : (
            <Button
              type="Button"
              className="delete-button"
              disabled={!audio.speed || !audio.description}
              onClick={() => {
                handleRegenerate(audio);
              }}
            >
              生成
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DuplicateAudio;
